import { PropsWithChildren } from "react";
import styled, { CSSProperties, css, keyframes } from "styled-components";
import {
  BACKGROUND_COLORS,
  BORDER_COLORS,
  COLORS,
  LAYER_COLORS,
} from "../../../constants/design/colors";
import { FONT_CSS } from "../../../constants/design/fontSize";

export const StrikeColumn = styled.div<{
  $width: number;
  activeBorderSide?: "left" | "right";
}>`
  ${FONT_CSS.data.three}
  width: ${(props) => props.$width}px;
  background-color: ${LAYER_COLORS.two};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;

  ${({ activeBorderSide }) => {
    if (activeBorderSide === "right") {
      return `
        border: 1px solid ${COLORS.blue.one};
        border-top-right-radius: 10px;
        border-bottom-right-radius: 10px;
        border-left: 1px solid transparent;
      `;
    }

    if (activeBorderSide === "left") {
      return `
        border: 1px solid ${COLORS.blue.one};
        border-top-left-radius: 10px;
        border-bottom-left-radius: 10px;
        border-right: 1px solid transparent;
      `;
    }

    return "border: 1px solid transparent";
  }};
`;

export const StrikeHeader = styled(StrikeColumn)``;

const RowBackground = styled.div.attrs<{
  $fillColor?: string;
  $fillPercent?: number;
  $fillTo?: "left" | "right";
}>(({ $fillColor, $fillPercent, $fillTo }) => {
  // Background
  if ($fillColor && $fillPercent) {
    // minimum of 1%
    const roundedFillPercent = Math.max(1, $fillPercent);

    return {
      style: {
        background: `linear-gradient(
          to ${$fillTo || "right"},
          ${$fillColor} ${roundedFillPercent || 100}%,
          transparent ${roundedFillPercent || 100}%,
          transparent 100%
        )`,
      },
    };
  }
  return {};
})<{
  $fillColor?: string;
  $fillPercent?: number;
  $fillTo?: "left" | "right";
}>`
  position: absolute;
  left: 0;
  width: 100%;
  top: 0;
  height: 100%;
  pointer-events: none;
`;

export const Row = styled.div<{
  $showBorderBottom?: boolean;
  $width?: string;
  $highlightOnHover?: boolean;
  $isActive?: boolean;
}>`
  position: relative;
  display: flex;
  width: ${({ $width }) => $width || "100%"};

  ${({ $isActive }) =>
    $isActive && `border: 1px solid ${COLORS.blue.one} !important;`}

  ${({ $highlightOnHover }) => {
    if ($highlightOnHover) {
      return css`
        &:hover {
          background-color: ${BACKGROUND_COLORS.four};
        }
      `;
    }
    return "";
  }}

  border-bottom: ${({ $showBorderBottom }) =>
    $showBorderBottom ? `1px solid ${BORDER_COLORS.one}` : "none"};

  span {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const flashAnimation = keyframes`
  0% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

const RowFlashAnimation = styled.div<{
  $fillColor?: string;
  $needsAnimation?: boolean;
}>`
  position: absolute;
  left: 0;
  width: 100%;
  height: 100%;
  ${({ $fillColor, $needsAnimation }) => {
    if ($fillColor) {
      if ($needsAnimation) {
        return css`
          background: ${$fillColor};
          &:before {
            content: " ";
            display: block;
            background: ${$fillColor};
            height: 100%;
            width: 100%;
          }
          opacity: 0;
          animation: ${flashAnimation} 0.6s ease-in-out forwards;
        `;
      }
    }
    return "";
  }}
`;

/**
 * A wrapper around Row.
 */
interface IRowWithSizeIndicatorProps {
  showBorderBottom?: boolean;
  fillColor?: string;
  fillPercent?: number;
  fillTo?: "left" | "right";
  width?: string;
  highlightOnHover?: boolean;
  needsAnimation?: boolean;
  style?: CSSProperties;
  onRowHover?: (hover: boolean) => void;
}

export function RowWithSizeIndicator({
  fillColor,
  fillPercent,
  fillTo,
  width,
  highlightOnHover,
  showBorderBottom,
  children,
  needsAnimation,
  onRowHover,
  ...others
}: PropsWithChildren<IRowWithSizeIndicatorProps>) {
  return (
    <Row
      $showBorderBottom={showBorderBottom}
      $width={width}
      $highlightOnHover={highlightOnHover}
      onMouseEnter={() => onRowHover?.(true)}
      onMouseLeave={() => onRowHover?.(false)}
      {...others}
    >
      <RowBackground
        $fillColor={fillColor}
        $fillPercent={fillPercent}
        $fillTo={fillTo}
      />
      {children}
      <RowFlashAnimation
        $fillColor={fillColor}
        $needsAnimation={needsAnimation}
      />
    </Row>
  );
}
